import React from 'react';
import { TaskTimer } from 'tasktimer';

import { CKToastContainer } from '../../components/CKToastContainer';
import { Toast } from '../../common/types';

interface ToastWithTimer extends Toast {
  timer?: TaskTimer;
}

type Action =
  | { type: 'add'; payload: ToastWithTimer }
  | { type: 'remove'; id: string }
  | { type: 'pause'; id: string }
  | { type: 'resume'; id: string };

type Dispatch = (action: Action) => void;

type State = { toasts: ToastWithTimer[] };

type CountProviderProps = { children: React.ReactNode };

const ToastStateContext = React.createContext<State | undefined>(undefined);

const ToastDispatchContext = React.createContext<Dispatch | undefined>(
  undefined
);

function removeById(array: Toast[], id: string) {
  return array.filter(el => el.id !== id);
}

function toastReducer(state: State, action: Action) {
  switch (action.type) {
    case 'add': {
      return {
        toasts: [
          ...state.toasts,
          {
            ...action.payload,
          },
        ],
      };
    }
    case 'pause': {
      const toast = state.toasts.find(t => t.id === action.id);
      if (toast && toast.timer) {
        toast?.timer?.pause();
      }

      return {
        toasts: state.toasts,
      };
    }
    case 'resume': {
      const toast = state.toasts.find(t => t.id === action.id);
      if (toast && toast.timer) {
        toast?.timer?.resume();
      }

      return {
        toasts: state.toasts,
      };
    }
    case 'remove': {
      return { toasts: removeById(state.toasts, action.id) };
    }
    default: {
      throw new Error(`Unhandled action type`);
    }
  }
}
export function ToastProvider({ children }: CountProviderProps) {
  const [state, dispatch] = React.useReducer(toastReducer, { toasts: [] });

  return (
    <ToastStateContext.Provider value={state}>
      <ToastDispatchContext.Provider value={dispatch}>
        {children}
        <CKToastContainer
          toasts={state.toasts}
          onClose={id => dispatch({ type: 'remove', id })}
          onEnter={id => dispatch({ type: 'pause', id })}
          onLeave={id => dispatch({ type: 'resume', id })}
        />
      </ToastDispatchContext.Provider>
    </ToastStateContext.Provider>
  );
}

export function useToast() {
  const dispatch = React.useContext(ToastDispatchContext);

  if (dispatch === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const makeTimerTask = (duration: number, id: string) => {
    const timerInstance = new TaskTimer(duration);
    timerInstance.add({
      id,
      totalRuns: 1,
      callback() {
        dispatch({ type: 'remove', id });
      },
    });
    timerInstance.start();
    return timerInstance;
  };

  const addToast = (
    toast: Pick<
      Toast,
      'autoHideDuration' | 'description' | 'heading' | 'icon' | 'variant'
    >
  ) => {
    const toastId = Math.random().toString();
    const timer = toast.autoHideDuration
      ? makeTimerTask(toast.autoHideDuration, toastId)
      : undefined;

    dispatch({
      type: 'add',
      payload: {
        id: toastId,
        ...toast,
        timer,
      },
    });
  };

  return {
    addToast,
  };
}
